import {
  getFoodRankMap,
  type FoodRankMap,
} from '@/utils/nutrition/getFoodRankMap';

import type {
  GetFoodRankResponse,
  GetAllFoodsResponse,
} from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';

import { Skeleton } from '@design-system/Skeleton';
import { MyFoodsCard } from '@design-system/Cards/MyFoodsCard';
import { DISPLAY, type DisplayMode } from './MyFoodsPage.model';

export interface MyFoodsPageFoodsGridProps {
  isLoading?: boolean;
  displayMode?: DisplayMode;
  foods?: GetAllFoodsResponse;
  foodRank?: GetFoodRankResponse;
  'data-testid'?: string;
}

const renderMyFoods = (
  foods: GetAllFoodsResponse,
  rankMap?: FoodRankMap,
  displayMode?: DisplayMode,
) => {
  const variant = displayMode === DISPLAY.GRID ? 'default' : 'flat';

  const renderedFoods = foods.map((food, index) => (
    <MyFoodsCard
      data-testid={`myFoods-foodCard-${index}`}
      food={food}
      key={`${food.title}-${food.id}-page`}
      variant={variant}
      rankMap={rankMap}
    />
  ));

  return renderedFoods;
};

export const MyFoodsPageFoodsGrid = ({
  foods,
  foodRank,
  isLoading,
  displayMode,
  'data-testid': testId,
}: MyFoodsPageFoodsGridProps) => {
  const rankMap = getFoodRankMap(foodRank);

  if (isLoading) {
    return (
      <div
        data-testid={testId ? `${testId}-loadingGrid` : 'myFoods-loadingGrid'}
        data-display-mode={displayMode}
        className='grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 data-[display-mode="list"]:lg:grid-cols-2'
      >
        {Array.from({ length: 24 }).map((_, index) => (
          <Skeleton
            key={`food-page-skeleton-${index}`}
            data-testid={testId ? `${testId}-skeleton-${index}` : `myFoods-foodSkeleton-${index}`}
            data-display-mode={displayMode}
            className="w-full h-64 data-[display-mode='list']:h-24"
          />
        ))}
      </div>
    );
  }

  if (!foods?.length) {
    return (
      <div 
        data-testid={testId ? `${testId}-noResults` : 'myFoods-noResults'}
        className="flex flex-col gap-4 py-14 text-center"
      >
        <span 
          data-testid={testId ? `${testId}-noResultsTitle` : 'myFoods-noResultsTitle'}
          className="typography-h3 text-primary"
        >
          No results found
        </span>

        <span 
          data-testid={testId ? `${testId}-noResultsMessage` : 'myFoods-noResultsMessage'}
          className="typography-body text-secondary"
        >
          Try adjusting your filters to see more results
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid={testId || 'myFoods-grid'}
      data-display-mode={displayMode}
      className='grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 data-[display-mode="list"]:lg:grid-cols-2'
    >
      {renderMyFoods(foods, rankMap, displayMode)}
    </div>
  );
};
